"use client";

import { cn, Table, TableSort, useSort } from "@kyan/ui";
import { AnimatePresence, motion } from "motion/react";

import { EmptyState } from "#/components/Contents/components/EmptyState/EmptyState";
import { useAutoExpandRows } from "#/hooks/useAutoExpandRows";
import { usePositionsDrawerMarket } from "#/store/app/positionsDrawer/positionsDrawerMarket";
import { useOpenOrders } from "#/store/wallet/account/openOrders";
import { type OpenOrder } from "#/types/orders";

import {
  defaultSort,
  innerColumns,
  outerColumns,
} from "./OptionOpenOrders.constants";
import { getOptionOpenOrderRows } from "./OptionOpenOrders.utils";

export interface OptionOpenOrdersProps {
  sort?: TableSort<OpenOrder>;
  isAllVariant?: boolean;
  isPortfolioVariant?: boolean;
  onSortChange?: (sort: TableSort<OpenOrder>) => void;
}

export const OptionOpenOrders: React.FC<OptionOpenOrdersProps> = ({
  sort: controlledSort,
  isAllVariant,
  isPortfolioVariant,
  onSortChange,
}) => {
  const { market } = usePositionsDrawerMarket();
  const { optionOpenOrders } = useOpenOrders();
  const { sort, setSort } = useSort<OpenOrder>({
    defaultSort,
    controlledSort,
    onSortChange,
  });

  const { rows, dataLength } = getOptionOpenOrderRows(optionOpenOrders, sort);
  const isEmpty = !rows.length;

  const { openRowIds, onRowClick } = useAutoExpandRows<OpenOrder>({
    dataLength,
  });

  return (
    <Table
      isServerSide
      data={rows}
      sort={sort}
      columns={outerColumns}
      openRowIds={openRowIds}
      defaultSort={defaultSort}
      hideHeader={isAllVariant}
      emptyState={
        <EmptyState
          underlying={market.underlying}
          description={`Your open ${market.underlying} option orders will show here`}
        />
      }
      classes={{
        wrapper: cn("bg-background flex flex-col", {
          "flex-1": !isAllVariant && !isEmpty,
          "h-full": isEmpty,
        }),
        table: cn("flex w-fit flex-col", {
          "flex border-b-0": isAllVariant,
          "flex-1 overflow-hidden": !isAllVariant && !isEmpty,
          "h-full": isEmpty,
          "border-b-0": isEmpty && !isPortfolioVariant,
        }),
        columnClassName: "py-2.25 items-center",
        thead: "sticky top-0 bg-buttonBg border-b border-divider",
        tbody: cn("text-textPrimary w-full", {
          "min-h-40 flex-1": isEmpty,
          "overflow-y-auto": !isEmpty && !isAllVariant,
          "border-divider border-b":
            !isEmpty && !isAllVariant && !isPortfolioVariant,
        }),
        row: cn("bg-buttonHover cursor-pointer", {
          "first:border-t-0": !isAllVariant,
        }),
        loading: {
          wrapper: "first:border-t-0",
        },
        cellClassName: "py-1.5",
      }}
      onSortChange={setSort}
      onRowClick={onRowClick}
      renderRowExpansion={({ row: { subOpenOrders = [] }, isOpen }) => (
        <AnimatePresence>
          {isOpen && subOpenOrders.length ? (
            <motion.div
              exit={{ height: 0 }}
              initial={{ height: 0 }}
              className="overflow-hidden"
              transition={{ duration: 0.3 }}
              animate={{ height: Math.min(subOpenOrders.length * 33, 400) }} // 400 is the max height set in TableVirtualizationWrapper
            >
              <Table
                hideHeader
                data={subOpenOrders}
                columns={innerColumns}
                virtualization={{
                  estimateSize: 33,
                  overscan: 12,
                }}
                classes={{
                  table: "border-b-0",
                  tbody: "text-textSecondary",
                  row: "bg-background",
                  cellClassName: "py-1.5",
                }}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
      )}
    />
  );
};
